import {Component, OnInit} from '@angular/core';
import {Router, RouterLink, RouterLinkActive, RouterOutlet} from '@angular/router';
import { NgStyle } from '@angular/common';
import {FormsModule} from '@angular/forms';
import {HubSession} from './model/hub-session';
import {HubApiService} from './services/hub-api.service';

const PUBLIC_FALLBACK_TRANSLATIONS: Record<string, string> = {
  LoginEmailLabel: 'Email',
  LoginPasswordLabel: 'Password',
  LoginButton: 'Login',
  HubLoading: 'Loading Goodvantage Hub...',
  HubLoginIntro: 'Sign in to manage donation forms, processes, translations, and NGO settings.',
  HubLoginFailedMessage: 'Login failed. Please check your email and password.',
  HubLogout: 'Logout'
};

@Component({
  selector: 'hub-root',
  imports: [RouterOutlet, RouterLink, RouterLinkActive, NgStyle, FormsModule],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css'
})
export class AppComponent implements OnInit {
  session: HubSession | null = null;
  login = '';
  password = '';
  loginError = '';
  loading = true;

  readonly navItems = [
    {route: '/forms', labelKey: 'DonationFormPageLink'},
    {route: '/processes', labelKey: 'ProcessPageLink'},
    {route: '/translations', labelKey: 'GlobalLabelsPageLink'},
    {route: '/swiss-qr-code', labelKey: 'SwissQrCodeLink'},
    {route: '/login-form', labelKey: 'loginFormLink'},
    {route: '/ebics', labelKey: 'EbicsLink', adminOnly: true},
    {route: '/settings', labelKey: 'NgoSettingsLink'},
    {route: '/users', labelKey: 'NgoUsersLink', adminOnly: true},
    {route: '/monitoring', labelKey: 'MonitoringLink', adminOnly: true}
  ];

  constructor(private hubApi: HubApiService, private router: Router) {
  }

  ngOnInit(): void {
    this.hubApi.session$.subscribe((session) => {
      this.session = session;
    });
    this.hubApi.loadSession().subscribe({
      next: () => this.loading = false,
      error: () => this.loading = false
    });
  }

  authenticate(): void {
    this.loginError = '';
    this.hubApi.login(this.login, this.password).subscribe({
      next: () => {
        this.password = '';
        void this.router.navigate(['/']);
      },
      error: () => {
        this.loginError = this.t('HubLoginFailedMessage');
      }
    });
  }

  logout(): void {
    this.hubApi.logout().subscribe({
      next: () => void this.router.navigate(['/'])
    });
  }

  setLanguage(language: string): void {
    this.hubApi.setLanguage(language).subscribe();
  }

  visibleNavItems(): Array<{ route: string; labelKey: string; adminOnly?: boolean }> {
    return this.navItems.filter((item) => !item.adminOnly || this.session?.user?.admin);
  }

  navigate(route: string): void {
    void this.router.navigate([route]);
  }

  currentNavRoute(): string {
    const url = this.router.url.split('?')[0].split('#')[0];
    const match = this.visibleNavItems().find((item) => url === item.route || url.startsWith(`${item.route}/`));
    return match?.route || '/';
  }

  t(key: string): string {
    return this.hubApi.translate(key) || PUBLIC_FALLBACK_TRANSLATIONS[key] || key;
  }

  get logoUrl(): string | null {
    if (!this.session?.ngo?.logoFileType) return null;
    return `//images.goodvanta.ge/logo/${this.session.ngo.id}.${this.session.ngo.logoFileType}`;
  }
}
